import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building2, 
  Users, 
  MapPin, 
  Phone, 
  Mail, 
  CreditCard,
  Search,
  Calendar,
  FileText,
  UserCheck,
  UserX,
  MapPinOff,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageCircle,
  Save,
  RefreshCw
} from 'lucide-react';
import { CompanyResource, CompanyUserResource, CompanyBranchResource, ProductResource, SaleResource, CompanySubscriptionResource } from '../types';
import api from '../services/api';
import { 
  getUserRoleColor, 
  getUserRoleLabel, 
  getBranchModeColor, 
  getBranchModeLabel,
  getBusinessTypeColor,
  getBusinessTypeLabel
} from '../utils/helpers';
import { ProductList, ProductFilters, Pagination, LoadingSpinner, SalesList, SalesFilters, CompanySubscriptionsList } from '../components';
import { useAuth } from '../contexts/AuthContext';

type TabType = 'users' | 'branches' | 'products' | 'sales' | 'subscriptions' | 'follow-up';

const CompanyDetail: React.FC = () => {
  const { canManage } = useAuth();
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [company, setCompany] = useState<CompanyResource | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Users tab state
  const [users, setUsers] = useState<CompanyUserResource[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersSearchInput, setUsersSearchInput] = useState('');

  // Branches tab state
  const [branches, setBranches] = useState<CompanyBranchResource[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchesPage, setBranchesPage] = useState(1);
  const [branchesPerPage, setBranchesPerPage] = useState(10);
  const [branchesTotal, setBranchesTotal] = useState(0);
  const [branchesSearch, setBranchesSearch] = useState('');
  const [branchesSearchInput, setBranchesSearchInput] = useState('');

  // Products tab state
  const [products, setProducts] = useState<ProductResource[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsPage, setProductsPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(12);
  const [productsTotal, setProductsTotal] = useState(0);
  const [productsSearch, setProductsSearch] = useState('');
  const [productsSearchInput, setProductsSearchInput] = useState('');
  const [productsFilters, setProductsFilters] = useState<{
    isBundle?: boolean | number;
    isActive?: boolean | number;
    isFavorite?: boolean | number;
  }>({});

  // Sales tab state
  const [sales, setSales] = useState<SaleResource[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesPage, setSalesPage] = useState(1);
  const [salesPerPage, setSalesPerPage] = useState(12);
  const [salesTotal, setSalesTotal] = useState(0);
  const [salesSearch, setSalesSearch] = useState('');
  const [salesSearchInput, setSalesSearchInput] = useState('');
  const [salesFilters, setSalesFilters] = useState<{
    status?: 'unpaid' | 'paid' | 'void';
    paymentMethod?: 'cash' | 'debit' | 'online' | 'qris';
    startDate?: string;
    endDate?: string;
    orderBy?: string;
  }>({});

  // Subscriptions tab state
  const [subscriptions, setSubscriptions] = useState<CompanySubscriptionResource[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);

  // Follow Up tab state
  const [isFollowUpDialogOpen, setIsFollowUpDialogOpen] = useState(false);
  const [followUpResult, setFollowUpResult] = useState('');
  const [followUpSubmitting, setFollowUpSubmitting] = useState(false);
  const [isPhoneValidityDialogOpen, setIsPhoneValidityDialogOpen] = useState(false);
  const [phoneValidity, setPhoneValidity] = useState<boolean | null>(null);
  const [phoneValiditySubmitting, setPhoneValiditySubmitting] = useState(false);
  const [lastFollowUpResult, setLastFollowUpResult] = useState<string | null>(null);
  const [lastFollowUpAt, setLastFollowUpAt] = useState<string | null>(null);

  useEffect(() => {
    if (companyId) {
      // Check if company data was passed via navigation state
      const companyFromState = location.state?.company;
      if (companyFromState && companyFromState.id === companyId) {
        setCompany(companyFromState);
        setLoading(false);
      } else {
        // No company data available, redirect to companies list
        setError('Company data not available. Please navigate from the companies list.');
        setLoading(false);
      }
    }
  }, [companyId, location.state]);

  useEffect(() => {
    if (activeTab === 'users' && companyId) {
      loadUsers();
    }
  }, [activeTab, companyId, usersPage, usersPerPage, usersSearch]);

  useEffect(() => {
    if (activeTab === 'branches' && companyId) {
      console.log('Branches tab activated, loading branches...');
      loadBranches();
    }
  }, [activeTab, companyId, branchesPage, branchesPerPage, branchesSearch]);

  useEffect(() => {
    if (activeTab === 'products' && companyId) {
      console.log('Products tab activated, loading products...');
      loadProducts();
    }
  }, [activeTab, companyId, productsPage, productsPerPage, productsSearch, productsFilters]);

  useEffect(() => {
    if (activeTab === 'sales' && companyId) {
      console.log('Sales tab activated, loading sales...');
      loadSales();
    }
  }, [activeTab, companyId, salesPage, salesPerPage, salesSearch, salesFilters]);

  useEffect(() => {
    if (activeTab === 'subscriptions' && companyId) {
      console.log('Subscriptions tab activated, loading subscriptions...');
      loadSubscriptions();
    }
  }, [activeTab, companyId]);

  // Initialize phone validity when company data is loaded
  useEffect(() => {
    if (company) {
      setPhoneValidity(company.isPhoneNumberValid);
      // Initialize follow up result from company data
      if (company.followedUpResult) {
        setLastFollowUpResult(company.followedUpResult);
      }
      if (company.followedUpAt) {
        setLastFollowUpAt(company.followedUpAt);
      }
    }
  }, [company]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUsersSearch(usersSearchInput);
      setUsersPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [usersSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setBranchesSearch(branchesSearchInput);
      setBranchesPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [branchesSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setProductsSearch(productsSearchInput);
      setProductsPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [productsSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSalesSearch(salesSearchInput);
      setSalesPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [salesSearchInput]);


  const loadUsers = async () => {
    if (!companyId) return;
    
    try {
      setUsersLoading(true);
      const response = await api.getCompanyUsers(companyId, usersPage, usersPerPage, usersSearch);
      setUsers(response.data);
      setUsersTotal(response.meta.total);
    } catch (error) {
      console.error('Error loading users:', error);
      // For demo purposes, show mock data
      setUsers([
        {
          id: '1',
          fullName: 'John Doe',
          email: 'john.doe@company.com',
          phone: '+62-812-3456-7890',
          userRole: 'admin',
          isRegisCompleted: true,
          companyBranch: {
            id: 'branch1',
            name: 'Main Branch',
            isMainBranch: true,
            branchMode: 'fnb',
            description: 'Primary location',
            isDisabledBySubscription: false
          }
        },
        {
          id: '2',
          fullName: 'Jane Smith',
          email: 'jane.smith@company.com',
          phone: '+62-812-3456-7891',
          userRole: 'cashier',
          isRegisCompleted: true,
          companyBranch: {
            id: 'branch2',
            name: 'Downtown Branch',
            isMainBranch: false,
              branchMode: 'retail',
              description: 'Downtown location',
              isDisabledBySubscription: false
            }
          }
        ]
      );
      setUsersTotal(2);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadBranches = async () => {
    if (!companyId) return;
    
    try {
      setBranchesLoading(true);
      console.log('Loading branches for company:', companyId);
      const response = await api.getCompanyBranches(companyId, branchesPage, branchesPerPage, branchesSearch);
      console.log('Branches response:', response);
      setBranches(response.data);
      setBranchesTotal(response.meta.total);
      console.log('Branches loaded:', response.data.length, 'Total:', response.meta.total);
    } catch (error) {
      console.error('Error loading branches:', error);
      // For demo purposes, show mock data
      setBranches([
        
      ]);
      setBranchesTotal(3);
    } finally {
      setBranchesLoading(false);
    }
  };

  const loadProducts = async () => {
    if (!companyId) return;
    
    try {
      setProductsLoading(true);
      console.log('Loading products for company:', companyId);
      const response = await api.getCompanyProducts(
        companyId, 
        productsPage, 
        productsPerPage, 
        productsSearch,
        productsFilters
      );
      console.log('Products response:', response);
      setProducts(response.data);
      setProductsTotal(response.meta.total);
      console.log('Products loaded:', response.data.length, 'Total:', response.meta.total);
    } catch (error) {
      console.error('Error loading products:', error);
      // For demo purposes, show mock data
      setProducts([
        
      ]);
      setProductsTotal(0);
    } finally {
      setProductsLoading(false);
    }
  };

  const loadSales = async () => {
    if (!companyId) return;
    
    try {
      setSalesLoading(true);
      console.log('Loading sales for company:', companyId);
      const response = await api.getCompanySales(
        companyId, 
        salesPage, 
        salesPerPage, 
        salesSearch,
        salesFilters
      );
      console.log('Sales response:', response);
      setSales(response.data);
      setSalesTotal(response.meta.total);
      console.log('Sales loaded:', response.data.length, 'Total:', response.meta.total);
    } catch (error) {
      console.error('Error loading sales:', error);
      // For demo purposes, show mock data
      setSales([
        
      ]);
      setSalesTotal(2);
    } finally {
      setSalesLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    if (!companyId) return;
    
    try {
      setSubscriptionsLoading(true);
      console.log('Loading subscriptions for company:', companyId);
      const response = await api.getCompanySubscriptions(companyId);
      console.log('Subscriptions response:', response);
      console.log('Response type:', typeof response);
      console.log('Is array:', Array.isArray(response));
      console.log('Response length:', response?.length);
      setSubscriptions(response);
      console.log('Subscriptions loaded:', response.length);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      // For demo purposes, show mock data based on real API response
      setSubscriptions([
        {
          id: '9f9140e2-d83c-4381-96f4-086864a366da',
          status: 'active',
          validUntil: '2025-08-09 23:59:59',
          maxBranch: 5,
          fnbMode: true,
          maxTransactionPerMonth: 0,
          maxTransactionPerDay: 10,
          description: 'Limited (Trial)',
          subscriptionId: '9e34f9a8-7f74-492c-8298-060d1296dce3',
          subscriptionInvoiceId: null,
          subscriptionLevel: 0,
          createdAt: '2025-08-06T06:34:36.000000Z'
        }
      ]);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const handleUsersPageChange = (page: number) => {
    setUsersPage(page);
  };

  const handleUsersPerPageChange = (newPerPage: number) => {
    setUsersPerPage(newPerPage);
    setUsersPage(1);
  };

  const handleBranchesPageChange = (page: number) => {
    setBranchesPage(page);
  };

  const handleBranchesPerPageChange = (newPerPage: number) => {
    setBranchesPerPage(newPerPage);
    setBranchesPage(1);
  };

  const handleProductsPageChange = (page: number) => {
    setProductsPage(page);
  };

  const handleProductsPerPageChange = (newPerPage: number) => {
    setProductsPerPage(newPerPage);
    setProductsPage(1);
  };

  const handleProductsFiltersChange = (filters: {
    isBundle?: boolean | number;
    isActive?: boolean | number;
    isFavorite?: boolean | number;
  }) => {
    setProductsFilters(filters);
    setProductsPage(1);
  };

  const handleProductsFiltersClear = () => {
    setProductsFilters({});
    setProductsPage(1);
  };

  const handleSalesPageChange = (page: number) => {
    setSalesPage(page);
  };

  const handleSalesPerPageChange = (newPerPage: number) => {
    setSalesPerPage(newPerPage);
    setSalesPage(1);
  };

  const handleSalesFiltersChange = (filters: {
    status?: 'unpaid' | 'paid' | 'void';
    paymentMethod?: 'cash' | 'debit' | 'online' | 'qris';
    startDate?: string;
    endDate?: string;
    orderBy?: string;
  }) => {
    setSalesFilters(filters);
    setSalesPage(1);
  };

  const handleSalesFiltersClear = () => {
    setSalesFilters({});
    setSalesPage(1);
  };

  const handleFollowUpSubmit = async () => {
    if (!companyId || !followUpResult.trim()) return;
    
    try {
      setFollowUpSubmitting(true);
      await api.followUpCompany(companyId, followUpResult.trim());
      // Update local state
      setLastFollowUpResult(followUpResult.trim());
      setLastFollowUpAt(new Date().toISOString());
      // Reload company data to get updated flags
      if (location.state?.company) {
        location.state.company.isFollowedUpBySales = true;
        setCompany({ ...location.state.company });
      }
      setIsFollowUpDialogOpen(false);
      setFollowUpResult('');
      // Show success message
      alert('Follow up result saved successfully!');
    } catch (error) {
      console.error('Error saving follow up result:', error);
      alert('Failed to save follow up result. Please try again.');
    } finally {
      setFollowUpSubmitting(false);
    }
  };

  const handleGetFollowUpUrl = async () => {
    if (!companyId) return;
    
    try {
      const data = await api.getCompanyFollowUpUrl(companyId);
      // Open WhatsApp URL in new tab
      window.open(data.followUpUrl, '_blank');
    } catch (error) {
      console.error('Error getting follow up URL:', error);
      alert('Failed to get follow up URL. Please try again.');
    }
  };

  const handleSetPhoneValidity = async (isValid: boolean) => {
    if (!companyId) return;
    
    try {
      setPhoneValiditySubmitting(true);
      await api.setCompanyPhoneValidity(companyId, isValid);
      // Update local state
      setPhoneValidity(isValid);
      if (location.state?.company) {
        location.state.company.isPhoneNumberValid = isValid;
        setCompany({ ...location.state.company });
      }
      setIsPhoneValidityDialogOpen(false);
      // Show success message
      alert(`Phone number marked as ${isValid ? 'valid' : 'invalid'} successfully!`);
    } catch (error) {
      console.error('Error setting phone validity:', error);
      alert('Failed to update phone validity. Please try again.');
    } finally {
      setPhoneValiditySubmitting(false);
    }
  };









  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Company Not Available</h2>
          <p className="text-gray-600 mb-6">
            {error || 'Please navigate to this page from the companies list to view company details.'}
          </p>
          <button
            onClick={() => navigate('/companies')}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
          >
            Back to Companies
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'branches', label: 'Company Branches', icon: MapPin },
    { id: 'products', label: 'Products', icon: Building2 },
    { id: 'sales', label: 'Sales', icon: CreditCard },
    { id: 'subscriptions', label: 'Company Subscriptions', icon: Calendar },
    { id: 'follow-up', label: 'Follow Up', icon: MessageCircle }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center">
                {company.brandLogo && (
                  <img 
                    src={company.brandLogo} 
                    alt={company.brandName}
                    className="h-10 w-10 rounded-lg object-cover mr-3"
                  />
                )}
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{company.companyName}</h1>
                  <p className="text-sm text-gray-500">{company.brandName}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBusinessTypeColor(company.businessType)}`}>
                {getBusinessTypeLabel(company.businessType)}
              </span>
              <span className="text-sm text-gray-500">{company.companyCode}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Company Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center">
              <MapPin className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Address</p>
                <p className="text-sm text-gray-500">{company.address || 'No address provided'}</p>
              </div>
            </div>
            <div className="flex items-center">
              <Phone className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Phone</p>
                <p className="text-sm text-gray-500">{company.companyPhone}</p>
              </div>
            </div>
            <div className="flex items-center">
              <Mail className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Admin Email</p>
                <p className="text-sm text-gray-500">{company.companyAdminEmail || 'No admin email'}</p>
              </div>
            </div>
          </div>

          {/* Subscription Info */}
          {company.activeSubscription && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-gray-900">Active Subscription</h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  company.activeSubscription.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {company.activeSubscription.status}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Level:</span>
                  <span className="ml-2 font-medium">{company.activeSubscription.subscriptionLevel}</span>
                </div>
                <div>
                  <span className="text-gray-500">Max Branches:</span>
                  <span className="ml-2 font-medium">{company.activeSubscription.maxBranch}</span>
                </div>
                <div>
                  <span className="text-gray-500">Monthly Sales:</span>
                  <span className="ml-2 font-medium">{company.activeSubscription.currentMonthlyPeriodSales.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-500">Valid Until:</span>
                  <span className="ml-2 font-medium">
                    {company.activeSubscription.validUntil 
                      ? new Date(company.activeSubscription.validUntil).toLocaleDateString()
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>
              {company.activeSubscription.description && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-start">
                    <FileText className="h-4 w-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600">
                      {company.activeSubscription.description}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabType)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'users' && (
              <div>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={usersSearchInput}
                        onChange={(e) => setUsersSearchInput(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Per page:</label>
                    <select
                      value={usersPerPage}
                      onChange={(e) => handleUsersPerPageChange(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {/* Users List */}
                {usersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600">Loading users...</span>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {usersSearch ? `No users match "${usersSearch}"` : 'This company has no users yet.'}
                    </p>
                  </div>
                ) : (
                  <div className="bg-white overflow-hidden">
                    <ul className="divide-y divide-gray-200">
                      {users.map((user) => (
                        <li key={user.id} className="px-4 py-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                  <span className="text-sm font-medium text-primary-600">
                                    {user.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="flex items-center">
                                  <h3 className="text-sm font-medium text-gray-900">{user.fullName}</h3>
                                  {user.isRegisCompleted ? (
                                    <UserCheck className="ml-2 h-4 w-4 text-green-500" />
                                  ) : (
                                    <UserX className="ml-2 h-4 w-4 text-yellow-500" />
                                  )}
                                </div>
                                <div className="flex items-center mt-1">
                                  <p className="text-sm text-gray-500">{user.email}</p>
                                  {user.phone && (
                                    <>
                                      <span className="mx-2 text-gray-300">•</span>
                                      <p className="text-sm text-gray-500">{user.phone}</p>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center mt-2">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUserRoleColor(user.userRole)}`}>
                                    {getUserRoleLabel(user.userRole)}
                                  </span>
                                  <span className="mx-2 text-gray-300">•</span>
                                  <div className="flex items-center text-xs text-gray-500">
                                    {user.companyBranch.isMainBranch ? (
                                      <MapPin className="h-3 w-3 mr-1 text-blue-500" />
                                    ) : (
                                      <MapPinOff className="h-3 w-3 mr-1 text-gray-400" />
                                    )}
                                    {user.companyBranch.name}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Users Pagination */}
                {usersTotal > 0 && (
                  <div className="mt-6 bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => handleUsersPageChange(usersPage - 1)}
                        disabled={usersPage === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => handleUsersPageChange(usersPage + 1)}
                        disabled={usersPage >= Math.ceil(usersTotal / usersPerPage)}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing{' '}
                          <span className="font-medium">{(usersPage - 1) * usersPerPage + 1}</span>
                          {' '}to{' '}
                          <span className="font-medium">
                            {Math.min(usersPage * usersPerPage, usersTotal)}
                          </span>
                          {' '}of{' '}
                          <span className="font-medium">{usersTotal.toLocaleString()}</span>
                          {' '}results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => handleUsersPageChange(usersPage - 1)}
                            disabled={usersPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          
                          {/* Page Numbers */}
                          {Array.from({ length: Math.min(7, Math.ceil(usersTotal / usersPerPage)) }, (_, i) => {
                            const page = i + 1;
                            const totalPages = Math.ceil(usersTotal / usersPerPage);
                            
                            if (page <= 3 || page > totalPages - 3 || (page >= usersPage - 1 && page <= usersPage + 1)) {
                              return (
                                <button
                                  key={page}
                                  onClick={() => handleUsersPageChange(page)}
                                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                    page === usersPage
                                      ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              );
                            } else if (page === 4 || page === totalPages - 3) {
                              return (
                                <span
                                  key={page}
                                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                                >
                                  ...
                                </span>
                              );
                            }
                            return null;
                          })}
                          
                          <button
                            onClick={() => handleUsersPageChange(usersPage + 1)}
                            disabled={usersPage >= Math.ceil(usersTotal / usersPerPage)}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'branches' && (
              <div>

                
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search branches..."
                        value={branchesSearchInput}
                        onChange={(e) => setBranchesSearchInput(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Per page:</label>
                    <select
                      value={branchesPerPage}
                      onChange={(e) => handleBranchesPerPageChange(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                                {/* Branches List */}
                {branchesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600">Loading branches...</span>
                  </div>
                ) : (
                  <>

                    
                    {branches.length === 0 ? (
                      <div className="text-center py-8">
                        <MapPin className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No branches found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {branchesSearch ? `No branches match "${branchesSearch}"` : 'This company has no branches yet.'}
                        </p>
                        <div className="mt-4 text-xs text-gray-400">
                          Debug: branches.length = {branches.length}, branchesTotal = {branchesTotal}
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                        {branches.map((branch) => (
                          <div key={branch.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            {/* Branch Header */}
                            <div className="p-4 border-b border-gray-200">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center min-w-0 flex-1">
                                  <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center mr-3 flex-shrink-0">
                                    <MapPin className="h-5 w-5 text-primary-600" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center">
                                      <h3 className="text-lg font-semibold text-gray-900 truncate">{branch.name}</h3>
                                      {branch.isMainBranch && (
                                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                          Main
                                        </span>
                                      )}
                                    </div>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getBranchModeColor(branch.branchMode)}`}>
                                      {getBranchModeLabel(branch.branchMode)}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4 flex-shrink-0">
                                  {branch.isDisabledBySubscription && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      Disabled
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Branch Details */}
                            <div className="p-4 space-y-3">
                              {branch.address && (
                                <div className="flex items-start text-sm text-gray-600">
                                  <MapPin className="h-4 w-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" />
                                  <span className="line-clamp-2">{branch.address}</span>
                                </div>
                              )}
                              {branch.phone && (
                                <div className="flex items-center text-sm text-gray-600">
                                  <Phone className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                                  <span>{branch.phone}</span>
                                </div>
                              )}
                              {branch.email && (
                                <div className="flex items-center text-sm text-gray-600">
                                  <Mail className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                                  <span className="truncate">{branch.email}</span>
                                </div>
                              )}
                              {branch.description && (
                                <div className="pt-2 border-t border-gray-100">
                                  <p className="text-xs text-gray-500 line-clamp-2">{branch.description}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Branches Pagination */}
                {branchesTotal > 0 && (
                  <div className="mt-6 bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => handleBranchesPageChange(branchesPage - 1)}
                        disabled={branchesPage === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => handleBranchesPageChange(branchesPage + 1)}
                        disabled={branchesPage >= Math.ceil(branchesTotal / branchesPerPage)}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing{' '}
                          <span className="font-medium">{(branchesPage - 1) * branchesPerPage + 1}</span>
                          {' '}to{' '}
                          <span className="font-medium">
                            {Math.min(branchesPage * branchesPerPage, branchesTotal)}
                          </span>
                          {' '}of{' '}
                          <span className="font-medium">{branchesTotal.toLocaleString()}</span>
                          {' '}results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => handleBranchesPageChange(branchesPage - 1)}
                            disabled={branchesPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          
                          {/* Page Numbers */}
                          {Array.from({ length: Math.min(7, Math.ceil(branchesTotal / branchesPerPage)) }, (_, i) => {
                            const page = i + 1;
                            const totalPages = Math.ceil(branchesTotal / branchesPerPage);
                            
                            if (page <= 3 || page > totalPages - 3 || (page >= branchesPage - 1 && page <= branchesPage + 1)) {
                              return (
                                <button
                                  key={page}
                                  onClick={() => handleBranchesPageChange(page)}
                                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                    page === branchesPage
                                      ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              );
                            } else if (page === 4 || page === totalPages - 3) {
                              return (
                                <span
                                  key={page}
                                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                                >
                                  ...
                                </span>
                              );
                            }
                            return null;
                          })}
                          
                          <button
                            onClick={() => handleBranchesPageChange(branchesPage + 1)}
                            disabled={branchesPage >= Math.ceil(branchesTotal / branchesPerPage)}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'products' && (
              <div>
                {/* Search and Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search products..."
                        value={productsSearchInput}
                        onChange={(e) => setProductsSearchInput(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Per page:</label>
                    <select
                      value={productsPerPage}
                      onChange={(e) => handleProductsPerPageChange(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value={12}>12</option>
                      <option value={24}>24</option>
                      <option value={48}>48</option>
                      <option value={96}>96</option>
                    </select>
                  </div>
                </div>

                {/* Product Filters */}
                <ProductFilters
                  filters={productsFilters}
                  onFilterChange={handleProductsFiltersChange}
                  onClearFilters={handleProductsFiltersClear}
                />

                {/* Products List */}
                <ProductList
                  products={products}
                  loading={productsLoading}
                  search={productsSearch}
                />

                {/* Products Pagination */}
                {productsTotal > 0 && (
                  <Pagination
                    currentPage={productsPage}
                    total={productsTotal}
                    perPage={productsPerPage}
                    onPageChange={handleProductsPageChange}
                  />
                )}
              </div>
            )}

            {activeTab === 'sales' && (
              <div>
                {/* Search and Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search sales by number, customer, or note..."
                        value={salesSearchInput}
                        onChange={(e) => setSalesSearchInput(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Per page:</label>
                    <select
                      value={salesPerPage}
                      onChange={(e) => handleSalesPerPageChange(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value={12}>12</option>
                      <option value={24}>24</option>
                      <option value={48}>48</option>
                      <option value={96}>96</option>
                    </select>
                  </div>
                </div>

                {/* Sales Filters */}
                <SalesFilters
                  filters={salesFilters}
                  onFilterChange={handleSalesFiltersChange}
                  onClearFilters={handleSalesFiltersClear}
                />

                {/* Sales List */}
                <SalesList
                  sales={sales}
                  loading={salesLoading}
                  search={salesSearch}
                />

                {/* Sales Pagination */}
                {salesTotal > 0 && (
                  <Pagination
                    currentPage={salesPage}
                    total={salesTotal}
                    perPage={salesPerPage}
                    onPageChange={handleSalesPageChange}
                  />
                )}
              </div>
            )}

            {activeTab === 'subscriptions' && (
              <div>
                {/* Subscriptions Header */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Company Subscriptions</h3>
                  <p className="text-sm text-gray-500">
                    Manage and view all subscription plans for this company.
                  </p>
                </div>

                {/* Subscriptions List */}
                                <CompanySubscriptionsList 
                  subscriptions={Array.isArray(subscriptions) ? subscriptions : []} 
                  loading={subscriptionsLoading}
                  companyId={companyId || ''}
                  onSubscriptionAssigned={loadSubscriptions}
                />
              </div>
            )}

            {activeTab === 'follow-up' && (
              <div>
                {/* Follow Up Header */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Follow Up Status</h3>
                  <p className="text-sm text-gray-500">
                    Track and manage follow-up activities for this company.
                  </p>
                </div>

                {/* Status Flags */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Need Follow Up Flag */}
                  <div className={`rounded-lg border-2 p-6 ${
                    company.isNeedFollowUp 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-green-300 bg-green-50'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900">Need Follow Up</h4>
                      {company.isNeedFollowUp ? (
                        <AlertCircle className="h-6 w-6 text-red-600" />
                      ) : (
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      )}
                    </div>
                    <p className={`text-2xl font-bold ${
                      company.isNeedFollowUp ? 'text-red-700' : 'text-green-700'
                    }`}>
                      {company.isNeedFollowUp ? 'Yes' : 'No'}
                    </p>
                    <p className="text-xs text-gray-600 mt-2">
                      {company.isNeedFollowUp 
                        ? 'This company requires follow up' 
                        : 'No follow up needed'}
                    </p>
                  </div>

                  {/* Phone Number Valid Flag */}
                  <div className={`rounded-lg border-2 p-6 ${
                    phoneValidity === null
                      ? 'border-gray-300 bg-gray-50'
                      : phoneValidity 
                        ? 'border-green-300 bg-green-50' 
                        : 'border-red-300 bg-red-50'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900">Phone Validity</h4>
                      {phoneValidity === null ? (
                        <AlertCircle className="h-6 w-6 text-gray-600" />
                      ) : phoneValidity ? (
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      ) : (
                        <XCircle className="h-6 w-6 text-red-600" />
                      )}
                    </div>
                    <p className={`text-2xl font-bold ${
                      phoneValidity === null
                        ? 'text-gray-700'
                        : phoneValidity 
                          ? 'text-green-700' 
                          : 'text-red-700'
                    }`}>
                      {phoneValidity === null ? 'Not Validated' : phoneValidity ? 'Valid' : 'Invalid'}
                    </p>
                    <p className="text-xs text-gray-600 mt-2">
                      {phoneValidity === null
                        ? 'Phone number not yet validated'
                        : phoneValidity 
                          ? 'Phone number is valid' 
                          : 'Phone number is invalid'}
                    </p>
                  </div>

                  {/* Followed Up by Sales Flag */}
                  <div className={`rounded-lg border-2 p-6 ${
                    company.isFollowedUpBySales 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-yellow-300 bg-yellow-50'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900">Sales Follow Up</h4>
                      {company.isFollowedUpBySales ? (
                        <CheckCircle className="h-6 w-6 text-blue-600" />
                      ) : (
                        <AlertCircle className="h-6 w-6 text-yellow-600" />
                      )}
                    </div>
                    <p className={`text-2xl font-bold ${
                      company.isFollowedUpBySales ? 'text-blue-700' : 'text-yellow-700'
                    }`}>
                      {company.isFollowedUpBySales ? 'Completed' : 'Pending'}
                    </p>
                    <p className="text-xs text-gray-600 mt-2">
                      {company.isFollowedUpBySales 
                        ? 'Sales team has followed up' 
                        : 'Awaiting sales follow up'}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                {canManage && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Actions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* WhatsApp Follow Up Button */}
                    <button
                      onClick={handleGetFollowUpUrl}
                      className="inline-flex items-center justify-center px-4 py-3 border border-green-600 text-sm font-medium rounded-md text-green-600 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                    >
                      <MessageCircle className="h-5 w-5 mr-2" />
                      WhatsApp Follow Up
                    </button>

                    {/* Set Follow Up Result Button */}
                    <button
                      onClick={() => setIsFollowUpDialogOpen(true)}
                      className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    >
                      <Save className="h-5 w-5 mr-2" />
                      Set Follow Up Result
                    </button>

                    {/* Validate Phone Button */}
                    <button
                      onClick={() => setIsPhoneValidityDialogOpen(true)}
                      className="inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors md:col-span-2"
                    >
                      <RefreshCw className="h-5 w-5 mr-2" />
                      Validate Phone Number
                    </button>
                  </div>
                </div>
                )}

                {/* Follow Up History */}
                {(lastFollowUpResult || company.followedUpResult || company.isFollowedUpBySales) && (
                  <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Follow Up History</h4>
                    {lastFollowUpResult || company.followedUpResult ? (
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-blue-900 mb-2">
                                Latest Follow Up Result
                                {(lastFollowUpAt || company.followedUpAt) && (
                                  <span className="ml-2 text-xs text-blue-600">
                                    ({new Date(lastFollowUpAt || company.followedUpAt || '').toLocaleString()})
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-blue-800 whitespace-pre-wrap">
                                {lastFollowUpResult || company.followedUpResult}
                              </p>
                              {company.followedUpBy && (
                                <div className="mt-3 pt-3 border-t border-blue-200">
                                  <p className="text-xs text-blue-700">
                                    <span className="font-medium">Followed up by:</span>{' '}
                                    {company.followedUpBy.fullName}
                                    {company.followedUpBy.email && (
                                      <span className="text-blue-600"> ({company.followedUpBy.email})</span>
                                    )}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : company.isFollowedUpBySales ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-blue-900">
                              This company has been followed up by sales team
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                              Follow up result not recorded in current session
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Follow Up Dialog */}
      {isFollowUpDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Set Follow Up Result
              </h3>
              <textarea
                value={followUpResult}
                onChange={(e) => setFollowUpResult(e.target.value)}
                placeholder="Enter follow up result..."
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autoFocus
              />
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsFollowUpDialogOpen(false);
                    setFollowUpResult('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  disabled={followUpSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleFollowUpSubmit}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={followUpSubmitting || !followUpResult.trim()}
                >
                  {followUpSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phone Validity Dialog */}
      {isPhoneValidityDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Validate Phone Number
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Current phone number: <span className="font-medium">{company.companyPhone}</span>
              </p>
              <p className="text-sm text-gray-600 mb-6">
                Is this phone number valid?
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleSetPhoneValidity(true)}
                  className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={phoneValiditySubmitting}
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  {phoneValiditySubmitting ? 'Saving...' : 'Valid'}
                </button>
                <button
                  onClick={() => handleSetPhoneValidity(false)}
                  className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={phoneValiditySubmitting}
                >
                  <XCircle className="h-5 w-5 mr-2" />
                  {phoneValiditySubmitting ? 'Saving...' : 'Invalid'}
                </button>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => {
                    setIsPhoneValidityDialogOpen(false);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDetail;
